import { useState, useEffect } from "react";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { createShareToken, revokeToken } from "../services/dashboardApi";
import ConfirmBox from "../../../library/common/ConfirmBox";
import Buttons from "../../../library/custom/button/Button";
import { InputField } from "../../../library/custom/textBox/InputField";
import ShareLinkResultDialog from "./ShareLinkResultDialog";
import "../reportDashboard.scss";

const buildDashboardUrl = (token) => (token ? `${window.location.origin}/d/${token}` : "");

// The ONLY mutating UI in the reportDashboard feature: generates/revokes rows in
// dashboard_tokens (Task 2's createShareToken/revokeToken). Everything else in this
// feature is read-only. Both mutations are gated behind an explicit confirm step using
// the project's existing ConfirmBox (library/common/ConfirmBox.jsx) rather than
// hand-rolled `role="dialog"` markup, per the brief's stated preference.
//
// Active-token state comes from the parent's dashboard payload (data.client.activeShareToken /
// shareTokenExpiresAt) instead of its own Token/List call — GetOrgAll already resolved the
// same active token server-side, so a second fetch here was a redundant DynamoDB scan on
// every page load.
const ShareLinkPanel = ({
  organisationId,
  clientName,
  accentColor,
  activeShareToken,
  shareTokenExpiresAt,
}) => {
  const [activeToken, setActiveToken] = useState(
    activeShareToken
      ? { token: activeShareToken, expiresAt: shareTokenExpiresAt, dashboardUrl: buildDashboardUrl(activeShareToken) }
      : null
  );
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'generate' | 'revoke', token? }
  const [isMutating, setIsMutating] = useState(false);
  const [mutationError, setMutationError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [resultDialog, setResultDialog] = useState(null); // { type: 'revoked' | 'generated', url? }

  useEffect(() => {
    setActiveToken(
      activeShareToken
        ? { token: activeShareToken, expiresAt: shareTokenExpiresAt, dashboardUrl: buildDashboardUrl(activeShareToken) }
        : null
    );
    setMutationError(null);
    setConfirmAction(null);
    setCopied(false);
    setResultDialog(null);
  }, [organisationId, activeShareToken, shareTokenExpiresAt]);

  const handleGenerate = async () => {
    setIsMutating(true);
    setMutationError(null);
    try {
      const userInfo = localStorage.getItem("userInfo");
      const createdBy = userInfo ? JSON.parse(userInfo)?.email : "admin";
      const result = await createShareToken({
        organisationId,
        clientName,
        accentColor,
        createdBy,
      });
      setActiveToken({ token: result.token, dashboardUrl: result.dashboardUrl });
      setConfirmAction(null);
      setResultDialog({ type: "generated", url: result.dashboardUrl });
    } catch (err) {
      setMutationError("Failed to generate share link. Please try again.");
    } finally {
      setIsMutating(false);
    }
  };

  const handleRevoke = async (token) => {
    setIsMutating(true);
    setMutationError(null);
    try {
      await revokeToken(token, organisationId);
      setActiveToken(null);
      setConfirmAction(null);
      setResultDialog({ type: "revoked" });
    } catch (err) {
      setMutationError("Failed to revoke share link. Please try again.");
    } finally {
      setIsMutating(false);
    }
  };

  const handleCopy = () => {
    const url = activeToken?.dashboardUrl || buildDashboardUrl(activeToken?.token);
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rdSharePanel" data-testid="share-link-panel">
      <div className="rdSharePanel__head">
        <span className="rdSharePanel__icon">
          <LinkIcon fontSize="small" />
        </span>
        <span className="rdSharePanel__title">Public Share Link</span>
      </div>

      {activeToken ? (
        <div className="rdSharePanel__row">
          <div className="rdSharePanel__field">
            <InputField
              name="shareLinkUrl"
              testId="shareLinkUrl"
              fullWidth
              disabled
              value={activeToken.dashboardUrl || buildDashboardUrl(activeToken.token)}
              onChange={() => {}}
            />
          </div>
          <Buttons
            id="btnCopyShareLink"
            label={copied ? "Copied!" : "Copy"}
            variantOutline
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={handleCopy}
          />
          <Buttons
            id="btnRevokeShareLink"
            label="Revoke"
            redButton
            disabled={isMutating}
            onClick={() => setConfirmAction({ type: "revoke", token: activeToken.token })}
          />
        </div>
      ) : (
        <Buttons
          id="btnGenerateShareLink"
          label="Generate New Link"
          varientContained
          disabled={isMutating}
          onClick={() => setConfirmAction({ type: "generate" })}
        />
      )}

      {mutationError && <div className="rdSharePanel__error">{mutationError}</div>}

      {confirmAction?.type === "generate" && (
        <ConfirmBox
          open
          header="Generate Share Link"
          content={`Generate a new public share link for ${clientName}? Anyone with this link can view this client's safety dashboard without logging in.`}
          handleCloseModal={() => !isMutating && setConfirmAction(null)}
          deletehandle={handleGenerate}
        />
      )}

      {confirmAction?.type === "revoke" && (
        <ConfirmBox
          open
          header="Revoke Share Link"
          content={`Revoke the active share link for ${clientName}? Existing recipients will lose access immediately.`}
          handleCloseModal={() => !isMutating && setConfirmAction(null)}
          deletehandle={() => handleRevoke(confirmAction.token)}
        />
      )}

      {resultDialog && (
        <ShareLinkResultDialog
          type={resultDialog.type}
          url={resultDialog.url}
          clientName={clientName}
          onClose={() => setResultDialog(null)}
        />
      )}
    </div>
  );
};

export default ShareLinkPanel;
